import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { Row, Col } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { SMLContainer } from '../smartLinks/smlContainer/SMLContainer';
import { SMLRouteNames } from '../../routing/sml/SMLRoutes';
import { SMLDatePicker } from './components/SMLDatePicker/SMLDatePicker';
import { useSMLDatePickerDateParam } from './hooks/useSMLDatePickerDateParam';
import { TrendsSectionCard } from './components/TrendsSectionCard/TrendsSectionCard';
import { TrendsSummaryPanel } from './components/TrendsSummaryPanel/TrendsSummaryPanel';
import { ChartsPageHead } from './charts/ChartsPageHead';
import { PageTabsInBody } from './components/SMLPageHead/PageTabsInBody';
import { ChartsTabRow } from './charts/ChartsTabRow';

const messages = defineMessages({
  groupArtists: { id: 'app.sml.trends.group.artists', defaultMessage: 'Artists' },
  groupTracks: { id: 'app.sml.trends.group.tracks', defaultMessage: 'Tracks' },
  artistsTrending: { id: 'app.sml.trends.section.artistsTrending', defaultMessage: 'Trending artists' },
  artistsUnusual: { id: 'app.sml.trends.section.artistsUnusual', defaultMessage: 'Unusual artist activity' },
  tracksTrending: { id: 'app.sml.trends.section.tracksTrending', defaultMessage: 'Trending tracks' },
  tracksUnusual: { id: 'app.sml.trends.section.tracksUnusual', defaultMessage: 'Unusual track activity' },
  breadcrumbTrends: { id: 'app.sml.breadcrumbs.trends', defaultMessage: 'Trends' },
  headerTitle: { id: 'app.sml.trends.header.title', defaultMessage: "What's moving, and why" },
  headerSubtitle: {
    id: 'app.sml.trends.header.subtitle',
    defaultMessage: 'AI-written daily read on where your followed catalogue is gaining, slipping, and breaking out.',
  },
});

const SMLTrendsViewComponent: React.FC = () => {
  const intl = useIntl();
  const { selectedDateValue } = useSMLDatePickerDateParam();

  return (
    <SMLContainer
      header={
        <ChartsPageHead tab={SMLRouteNames.trends}>
          <SMLDatePicker showRangePresets />
        </ChartsPageHead>
      }
    >
      <PageTabsInBody>
        <ChartsTabRow screen={SMLRouteNames.trends} />
      </PageTabsInBody>
      <TrendsSummaryPanel date={selectedDateValue} />
      <h2 className="h4 mb-3">{intl.formatMessage(messages.groupArtists)}</h2>
      <Row className="mb-4">
        <Col xs={12} xl={6} className="mb-4 mb-xl-0">
          <TrendsSectionCard
            title={messages.artistsTrending}
            category="trendingUp"
            entityType="artist"
            paginationParamName="artistsTrendingPage"
          />
        </Col>
        <Col xs={12} xl={6}>
          <TrendsSectionCard
            title={messages.artistsUnusual}
            category="anomalies"
            entityType="artist"
            paginationParamName="artistsUnusualPage"
          />
        </Col>
      </Row>

      <h2 className="h4 mb-3">{intl.formatMessage(messages.groupTracks)}</h2>
      <Row className="mb-4">
        <Col xs={12} xl={6} className="mb-4 mb-xl-0">
          <TrendsSectionCard
            title={messages.tracksTrending}
            category="trendingUp"
            entityType="track"
            paginationParamName="tracksTrendingPage"
          />
        </Col>
        <Col xs={12} xl={6}>
          <TrendsSectionCard
            title={messages.tracksUnusual}
            category="anomalies"
            entityType="track"
            paginationParamName="tracksUnusualPage"
          />
        </Col>
      </Row>
    </SMLContainer>
  );
};

export const SMLTrendsView = TypedMemo(SMLTrendsViewComponent);
